import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Volume2, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { withAudioVersion } from '../utils/audioUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DarijaSounds = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [understood, setUnderstood] = useState(false);

  useEffect(() => {
    fetchSounds();
  }, []);

  const fetchSounds = async () => {
    try {
      const response = await axios.get(`${API}/darija-sounds`);
      setSounds(response.data);
    } catch (error) {
      console.error('Failed to fetch sounds:', error);
      toast.error(language === 'en' ? 'Error loading sounds' : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (!understood) {
      toast.error(language === 'en' 
        ? 'Please confirm you understood the sounds' 
        : 'Veuillez confirmer que vous avez compris les sons');
      return;
    }
    localStorage.setItem('darija_sounds_completed', 'true');
    navigate('/dashboard');
    toast.success(language === 'en' 
      ? 'Great! You can now start Level 1!' 
      : 'Excellent ! Vous pouvez maintenant commencer le Niveau 1 !');
  };

  const playAudio = (audioUrl) => {
    if (audioUrl) {
      // Construct full URL if it's a relative path
      const fullUrl = withAudioVersion(audioUrl.startsWith('http') ? audioUrl : `${BACKEND_URL}${audioUrl}`);
      const audio = new Audio(fullUrl);
      audio.play().catch((err) => {
        console.error('Audio playback error:', err);
        toast.info(language === 'en' ? 'Could not play audio' : 'Impossible de lire l\'audio');
      });
    } else {
      toast.info(language === 'en' ? 'Audio not yet recorded' : 'Audio pas encore enregistré');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-orange-600 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
            className="mb-4 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-nunito">{t('back')}</span>
          </button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-3">
              {t('soundsTitle')}
            </h1>
            <p className="font-nunito text-base sm:text-xl opacity-90">
              {t('soundsSubtitle')}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-5xl">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-accent/10 border-2 border-accent/30 rounded-2xl p-5 sm:p-6 mb-8"
        >
          <h2 className="font-playfair text-xl sm:text-2xl font-bold text-charcoal mb-4 flex items-center gap-2">
            <span>💡</span> {t('soundsIntroTitle')}
          </h2>
          <p className="font-nunito text-base sm:text-lg text-charcoal leading-relaxed">
            {t('soundsIntroText')}
          </p>
        </motion.div>

        {/* Sounds List */}
        <div className="space-y-6 mb-8">
          {sounds.map((sound, index) => (
            <motion.div
              key={sound.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-stone-200 p-5 sm:p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex flex-col sm:flex-row items-start gap-4">
                {/* Letter Circle */}
                <div className="bg-primary text-white rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-xl sm:text-2xl">{sound.letter.split(' ')[0]}</span>
                </div>

                <div className="flex-1 w-full">
                  {/* Title */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                    <div>
                      <h3 className="font-playfair text-xl sm:text-2xl font-bold text-charcoal">
                        {sound.letter}
                      </h3>
                      <p className="font-nunito text-sm text-charcoal/60">
                        {t('phonetic')} : <strong>{sound.phonetic}</strong>
                      </p>
                    </div>
                    {/* Audio Buttons */}
                    <div className="flex items-center gap-3">
                      {/* Son Isolé */}
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => playAudio(sound.audio_url)}
                          data-testid={`play-sound-${index}`}
                          className={`tile-button p-3 rounded-full shadow-[0_3px_0_#004d40] ${
                            sound.audio_url 
                              ? 'bg-secondary text-white hover:shadow-lg' 
                              : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                          }`}
                          disabled={!sound.audio_url}
                        >
                          <Volume2 className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                        <span className="text-xs font-nunito text-charcoal/60 mt-1">
                          {language === 'en' ? 'Sound' : 'Son'}
                        </span>
                      </div>
                      
                      {/* Son en Mot */}
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => playAudio(sound.audio_example_url)}
                          data-testid={`play-example-${index}`}
                          className={`tile-button p-3 rounded-full shadow-[0_3px_0_#c2410c] ${
                            sound.audio_example_url 
                              ? 'bg-orange-500 text-white hover:shadow-lg' 
                              : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                          }`}
                          disabled={!sound.audio_example_url}
                        >
                          <span className="text-lg">📝</span>
                        </button>
                        <span className="text-xs font-nunito text-charcoal/60 mt-1">
                          {language === 'en' ? 'In Word' : 'En mot'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-sand rounded-lg p-4 mb-4">
                    <p className="font-nunito text-sm sm:text-base text-charcoal leading-relaxed">
                      {language === 'en' ? sound.description_en : sound.description_fr}
                    </p>
                  </div>

                  {/* Examples */}
                  <div>
                    <p className="font-nunito font-bold text-sm text-charcoal/70 mb-2">
                      📝 {t('examples')} :
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(language === 'en' ? sound.examples_en : sound.examples_fr)?.map((example, i) => (
                        <span
                          key={i}
                          className="bg-accent/20 text-accent font-nunito font-semibold px-3 py-1 rounded-lg text-sm"
                        >
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Confirmation & Continue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-2xl shadow-lg border-2 border-stone-200 p-6 sm:p-8"
        >
          <div className="flex items-start gap-4 mb-6">
            <input
              type="checkbox"
              id="understood"
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              data-testid="understood-checkbox"
              className="w-6 h-6 text-primary rounded focus:ring-primary mt-1 cursor-pointer"
            />
            <label htmlFor="understood" className="font-nunito text-base sm:text-lg text-charcoal cursor-pointer">
              ✅ {t('understood')}
            </label>
          </div>

          <button
            onClick={handleContinue}
            disabled={!understood}
            data-testid="continue-btn"
            className={`w-full tile-button font-nunito font-bold py-4 px-6 rounded-xl text-lg flex items-center justify-center gap-2 ${
              understood
                ? 'bg-primary text-white shadow-[0_4px_0_#9a360b] hover:shadow-lg'
                : 'bg-stone-300 text-stone-500 cursor-not-allowed'
            }`}
          >
            <CheckCircle className="w-6 h-6" />
            {t('continueToLevel')}
          </button>
        </motion.div>

        {/* Tip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center mt-6"
        >
          <p className="font-nunito text-sm text-charcoal/60">
            💡 {t('soundsTip')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default DarijaSounds;
